import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type MutableRefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDelayedPending } from "@/hooks/use-delayed-pending";
import { fetchShopSyncRun } from "@/lib/amazon-api";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import { refreshProductListings } from "@/lib/product-listing-api";
import { SSE_FALLBACK_POLL_MS, subscribeToSseInvalidation } from "@/lib/sse-invalidation";
import { isProductSyncEvent } from "./product-listing-helpers";

interface UseProductCatalogSyncOptions {
  shopId: string;
  mounted: MutableRefObject<boolean>;
  setRefreshStreamFailed: (failed: boolean) => void;
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  onInventoryReady?: () => void;
  showInventoryRefresh: boolean;
  refreshInventoryOnMount: boolean;
}

/**
 * Keeps product reads fresh from the Shop sync stream and drives the catalog refresh run
 * (manual, retried or automatic on mount) until it settles.
 */
export function useProductCatalogSync({
  shopId,
  mounted,
  setRefreshStreamFailed,
  onInvalidProductsChange,
  onInventoryReady,
  showInventoryRefresh,
  refreshInventoryOnMount,
}: UseProductCatalogSyncOptions) {
  const { t } = useTranslation("campaignCreation");
  const queryClient = useQueryClient();
  const [manualRefreshStarted, setManualRefreshStarted] = useState(false);
  const [refreshRunId, setRefreshRunId] = useState<string | null>(null);
  const [refreshFailure, setRefreshFailure] = useState<string | null>(null);
  const [refreshPartial, setRefreshPartial] = useState(false);
  const [refreshSkipped, setRefreshSkipped] = useState<string | null>(null);
  const automaticRefreshStarted = useRef(false);

  useEffect(() => {
    if (!shopId) return;
    // Subscribe once per Shop with prefix keys: re-subscribing on every page, filter, sort or
    // expand change would reopen the stream and re-invalidate the rows that were just fetched.
    const stream = {
      id: `shop-sync:${shopId}`,
      url: `/api/v1/sync/shops/${encodeURIComponent(shopId)}/events`,
      eventNames: ["run.changed"] as const,
      exact: false,
      shouldInvalidate: (event: Event) => isProductSyncEvent(event),
    };
    const stopGroups = subscribeToSseInvalidation(queryClient, {
      ...stream,
      queryKey: ["product-listings", "groups", shopId],
      onConnectionChange: (state) => setRefreshStreamFailed(state === "error"),
    });
    const stopListings = subscribeToSseInvalidation(queryClient, {
      ...stream,
      queryKey: ["product-listings", "list", shopId],
    });
    return () => {
      stopGroups();
      stopListings();
    };
  }, [queryClient, setRefreshStreamFailed, shopId]);

  const refreshStatus = useQuery({
    queryKey: ["product-listings", "refresh-status", shopId, refreshRunId],
    queryFn: () => fetchShopSyncRun(shopId, refreshRunId!),
    enabled: Boolean(refreshRunId),
    retry: false,
    refetchInterval: (query) => {
      const run = query.state.data;
      return run && run.status !== "running" ? false : SSE_FALLBACK_POLL_MS;
    },
  });

  useEffect(() => {
    if (!shopId || !refreshRunId) return;
    return subscribeToSseInvalidation(queryClient, {
      id: `shop-sync:${shopId}`,
      url: `/api/v1/sync/shops/${encodeURIComponent(shopId)}/events`,
      eventNames: ["run.changed"],
      queryKey: ["product-listings", "refresh-status", shopId, refreshRunId],
      onConnectionChange: (state) => setRefreshStreamFailed(state === "error"),
      shouldInvalidate: (event) => isProductSyncEvent(event, refreshRunId),
    });
  }, [queryClient, refreshRunId, setRefreshStreamFailed, shopId]);

  useEffect(() => {
    const run = refreshStatus.data;
    if (!refreshRunId || !run || run.status === "running") return;
    setRefreshRunId(null);
    if (run.status === "skipped") {
      setRefreshFailure(null);
      setRefreshSkipped(
        t(
          run.error === "amazon.sync.product.another_run_active"
            ? "products.quiet.alreadyRunning"
            : "products.quiet.skipped",
        ),
      );
      setManualRefreshStarted(false);
      return;
    }
    if (run.status === "success" || run.status === "partial") {
      setRefreshFailure(null);
      setRefreshPartial(run.status === "partial");
      if (run.status === "success") onInvalidProductsChange([]);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["product-listings", "groups", shopId] }),
        queryClient.invalidateQueries({ queryKey: ["product-listings", "list", shopId] }),
      ]).then(() => {
        if (!mounted.current) return;
        setManualRefreshStarted(false);
        if (run.status === "success") onInventoryReady?.();
      });
      return;
    }
    const message = t("products.quiet.failed");
    setRefreshFailure(message);
    setManualRefreshStarted(false);
    if (showInventoryRefresh) toast.error(message);
  }, [
    mounted,
    onInvalidProductsChange,
    onInventoryReady,
    queryClient,
    refreshStatus.data,
    refreshRunId,
    shopId,
    showInventoryRefresh,
    t,
  ]);

  const refresh = useMutation({
    mutationFn: () => refreshProductListings(shopId),
    onMutate: () => {
      setRefreshPartial(false);
      setRefreshSkipped(null);
      setRefreshFailure(null);
    },
    onSuccess: ({ runId }) => {
      if (!mounted.current) return;
      setRefreshFailure(null);
      setRefreshPartial(false);
      setRefreshRunId(runId);
    },
    onError: () => {
      if (!mounted.current) return;
      const message = t("products.quiet.failed");
      setRefreshFailure(message);
      setManualRefreshStarted(false);
      if (showInventoryRefresh) toast.error(message);
    },
  });

  useEffect(() => {
    if (!refreshInventoryOnMount || automaticRefreshStarted.current) return;
    automaticRefreshStarted.current = true;
    refresh.mutate();
  }, [refresh.mutate, refreshInventoryOnMount]);

  // Keep the table busy through the queued sync and subsequent row reads.
  const productRefreshLoading = useDelayedPending(
    manualRefreshStarted && !refreshStatus.isError,
    0,
    300,
  );

  function startManualRefresh() {
    setManualRefreshStarted(true);
    refresh.mutate();
  }
  /** Retries whichever step failed: polling the queued run, or queueing the run itself. */
  function retryBlockedRefresh() {
    if (refreshRunId && refreshStatus.isError) {
      void refreshStatus.refetch();
    } else {
      automaticRefreshStarted.current = true;
      refresh.mutate();
    }
  }

  return {
    refresh,
    refreshStatus,
    refreshRunId,
    refreshFailure,
    refreshPartial,
    refreshSkipped,
    refreshPending: refresh.isPending || (refreshRunId != null && !refreshStatus.isError),
    manualRefreshStarted,
    productRefreshLoading,
    startManualRefresh,
    retryBlockedRefresh,
  };
}

export type ProductCatalogSync = ReturnType<typeof useProductCatalogSync>;
